import React from 'react'
import { Navigate } from 'react-router-dom'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { RefreshCw } from 'lucide-react'

/**
 * FeatureGuard Component
 * 
 * Protects routes based on feature flags. Only renders children if the required feature is enabled.
 * 
 * Key behavior:
 * - If features are still loading: show loading UI, don't block
 * - If features are not loaded (empty): allow access (backward compatibility)
 * - If features are loaded and explicitly disabled: block access
 * - If features are loaded and enabled: allow access
 * 
 * @param {Object} props
 * @param {string} props.feature - Feature key to check
 * @param {React.ReactNode} props.children - Children to render if feature is enabled
 * @param {React.ReactNode} props.fallback - Optional fallback component if feature is disabled
 * @param {string} props.redirectTo - Optional redirect path if feature is disabled (default: /dashboard)
 */
function FeatureGuard({ feature, children, fallback, redirectTo = '/dashboard' }) {
  const { isFeatureEnabled, loading, error, features } = useFeatureFlag()

  console.log(`[FEATURE_GUARD] Rendering for feature: "${feature}", loading: ${loading}, featuresCount: ${features ? Object.keys(features).length : 0}`);

  // Show loading state
  if (loading) {
    console.log(`[FEATURE_GUARD] Feature "${feature}" - LOADING (features still fetching, ALLOWING access)`);
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <div className="text-gray-600">Loading...</div>
        </div>
      </div>
    )
  }

  // If features are not loaded (empty object), allow access (backward compatibility)
  const featuresNotLoaded = !features || Object.keys(features).length === 0
  if (featuresNotLoaded) {
    console.log(`[FEATURE_GUARD] Feature "${feature}" - FEATURES_NOT_LOADED (features empty/undefined, ALLOWING access)`);
    return <>{children}</>
  }

  // Show error state
  if (error) {
    console.log(`[FEATURE_GUARD] Feature "${feature}" - ERROR: ${error} (ALLOWING access due to error)`);
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 mb-2">Error loading feature configuration</div>
          <div className="text-sm text-gray-500">{error}</div>
        </div>
      </div>
    )
  }

  // Features are loaded - check if feature is explicitly enabled
  const enabled = isFeatureEnabled(feature)

  console.log(`[FEATURE_GUARD] Feature "${feature}" - features loaded, enabled: ${enabled}`);

  if (!enabled) {
    // Render fallback if provided
    if (fallback) {
      console.log(`[FEATURE_GUARD] Feature "${feature}" - BLOCKED (feature is explicitly disabled, rendering fallback)`);
      return fallback
    }

    // Otherwise redirect
    console.log(`[FEATURE_GUARD] Feature "${feature}" - BLOCKED (feature is explicitly disabled, redirecting to: ${redirectTo})`);
    return <Navigate to={redirectTo} replace />
  }

  // Feature is enabled, render children
  console.log(`[FEATURE_GUARD] Feature "${feature}" - ALLOWED (feature is enabled)`);
  return <>{children}</>
}

export default FeatureGuard
